// What to say, and what to offer, once a project's files are gone under the retention policy.
// One place, because a client reads it in the drawer, the review player, the files tab and the
// photo grid, and a staffer reads the same plus a Restore button; five surfaces saying it five
// ways is how one of them ends up saying "pending_permanent_deletion" to a client.
//
// Files are soft-deleted 180 days after the client approves (app/retention.py): the Stream copy
// goes at once, the originals sit in cold storage for 30 days, and an admin or staffer can bring
// them back in that window. After it, they are gone for good.

const SOFT = 'pending_permanent_deletion';
const CONTACT = 'hello@uncutgems.com';

function day(s) {
  const d = s ? new Date(s) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
}

export function filesRemoved(project) {
  return !!project && (project.files_recovery_state === SOFT || !!project.files_purged);
}

/** Whole days left in the restore window, counting today: 0 on the last day, negative once closed. */
export function daysLeft(project, now = new Date()) {
  const until = project?.scheduled_hard_delete_at ? new Date(project.scheduled_hard_delete_at) : null;
  if (!until || Number.isNaN(until.getTime())) return null;
  return Math.floor((until.getTime() - now.getTime()) / 86400000);
}

export function daysLeftWords(n) {
  if (n <= 0) return 'today is the last day';
  return n === 1 ? '1 day left' : `${n} days left`;
}

/** null for a live project; otherwise what to tell everyone, in a sentence. */
export function retentionNotice(project, now = new Date()) {
  if (!filesRemoved(project)) return null;
  if (project.files_purged || project.files_recovery_state !== SOFT) {
    const on = day(project.files_hard_deleted_at || project.files_soft_deleted_at);
    return { state: 'gone', date: on,
      text: `These files were removed under our retention policy${on ? ` on ${on}` : ''} and cannot be recovered. If you have a question about this project, contact us at ${CONTACT}.` };
  }
  const on = day(project.files_soft_deleted_at);
  return { state: 'removed', date: on, until: day(project.scheduled_hard_delete_at), daysLeft: daysLeft(project, now),
    text: `These files were removed under our retention policy${on ? ` on ${on}` : ''}. If you need them, contact us at ${CONTACT} and we can bring them back.` };
}

/** The Restore button, for admin and staff, only while there is something to restore and time to do it. */
export function restoreButton(project, role, now = new Date()) {
  if (role !== 'admin' && role !== 'staff') return null;
  if (!project || project.files_recovery_state !== SOFT || project.files_purged) return null;
  const left = daysLeft(project, now);
  if (left === null || left < 0) return null;
  const until = day(project.scheduled_hard_delete_at);
  return { label: `Restore files: ${daysLeftWords(left)}`, daysLeft: left,
    title: `The files are in cold storage until ${until}. Restore brings them back to normal: the originals online again and the review copy re-made. Nothing to confirm; this is the safe direction.` };
}
